import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { Input } from '@components/Inputs/Input/Input';

import styles from './CheckboxDropdown.module.scss';

const languageNamespace = 'checkbox-dropdown';

export type CheckboxDropdownItem = {
  id: string | number;
  label: string | number;
};

export type ItemMap = Map<string | number, CheckboxDropdownItem>;

export type CheckboxDropdownProps = {
  /**
   * Provide a name if you're using this inside a form
   */
  name?: string;
  /**
   * Label to show
   */
  label?: string;
  /**
   * Should the label update to display the currently selected items. E.g. "Item1, Item2, Item3..."
   */
  shouldLabelShowSelectedItems?: boolean;
  /**
   * Custom style class to extend the checkbox
   */
  className?: string;
  /**
   * Flag to disable the checkbox
   */
  disabled?: boolean;
  /**
   * Test id for Cypress and other test suites, maps to `data-testid`
   */
  testId?: string;
  labelTestId?: string;
  value?: ItemMap;
  defaultValue?: ItemMap;
  items?: CheckboxDropdownItem[];
  onChange?: (values: ItemMap) => void;
  /**
   * This stops the Dropdown closing when you click apply
   */
  keepOpenOnApply?: boolean;
  showSelectAllOption?: boolean;
  showFreeTextInput?: boolean;
  validateInput?: (input: string) => boolean;
  /**
   * validation function to check free text input
   */
};

type updateSelectedReducer =
  | { items: CheckboxDropdownItem[]; state: boolean; replace?: false }
  | {
      items: ItemMap;
      replace: true;
    };

const updateSelectedReducer = (existingMap: ItemMap, data: updateSelectedReducer) => {
  const { items, replace } = data;
  if (replace) {
    if (items instanceof Map) {
      return items;
    } else {
      return new Map<string | number, CheckboxDropdownItem>();
    }
  }

  items.forEach((item) => {
    if (data?.state) existingMap.set(item.id, item);
    else {
      existingMap.delete(item.id);
    }
  });
  return existingMap;
};

const formatLabel = (
  defaultLabel: string | undefined,
  shouldLabelShowSelectedItems?: boolean,
  selected?: ItemMap,
): string | undefined => {
  if (!shouldLabelShowSelectedItems || !selected?.size) return defaultLabel;

  let combinedLabel = '';
  const values = Array.from(selected.values());
  values.forEach(({ label }, index) => {
    if (index !== 0) {
      combinedLabel += ', ';
    }
    combinedLabel += label;
  });
  return combinedLabel;
};

const CheckboxDropdown = ({
  className,
  disabled,
  testId,
  labelTestId,
  label,
  shouldLabelShowSelectedItems,
  value,
  defaultValue,
  keepOpenOnApply,
  onChange,
  items,
  showSelectAllOption,
  showFreeTextInput,
  validateInput,
  name,
}: CheckboxDropdownProps) => {
  const [selected, updateSelected] = useReducer<
    (existingMap: ItemMap, data: updateSelectedReducer) => ItemMap
  >(updateSelectedReducer, defaultValue || new Map());

  const [searchInput, setSearchInput] = useState<string>('');
  const [inputErrorState, setInputErrorState] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation(languageNamespace);

  const filteredItems = useMemo(
    () =>
      showFreeTextInput
        ? items?.filter((item) =>
            item.label.toString().toLowerCase().includes(searchInput.toLowerCase()),
          )
        : items,
    [items, searchInput, showFreeTextInput],
  );

  const selectAll = useCallback(() => {
    const allItemsSelected = filteredItems?.every((item) => selected.has(item.id));
    if (allItemsSelected) {
      updateSelected({ items: new Map(), replace: true });
    } else {
      const itemsMap = new Map(filteredItems?.map((item) => [item.id, item]));
      updateSelected({ items: itemsMap, replace: true });
    }
  }, [filteredItems, selected]);

  const toggleIsOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleApply = () => {
    onChange?.(selected);
    if (!keepOpenOnApply) toggleIsOpen();
  };

  const renderItem = useCallback(
    (item: CheckboxDropdownItem) => {
      const isSelected = selected?.has(item?.id);
      return (
        <li key={item.id}>
          <Checkbox
            className={styles.checkbox}
            labelTextClassName={styles.checkboxLabel}
            label={`${item.label}`}
            onChange={(value) => updateSelected({ items: [item], state: value })}
            provideValueOnChange
            checked={isSelected}
            defaultChecked={isSelected}
            disabled={disabled}
          />
        </li>
      );
    },
    [disabled, selected],
  );

  const renderItems = useCallback(() => {
    return (
      <>
        <>
          {showSelectAllOption && (
            <li>
              <Checkbox
                className={styles.checkbox}
                labelTextClassName={styles.checkboxLabel}
                label={t('selectAll')}
                onChange={selectAll}
                provideValueOnChange
                checked={items?.every((item) => selected.has(item.id))}
                disabled={disabled}
              />
            </li>
          )}
          {filteredItems?.map(renderItem)}
        </>
      </>
    );
  }, [
    disabled,
    filteredItems,
    inputErrorState,
    items,
    renderItem,
    selectAll,
    selected,
    showFreeTextInput,
    showSelectAllOption,
    t,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        if (!event.target || !containerRef?.current?.contains(event?.target as Node)) {
          toggleIsOpen();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleIsOpen]);

  useEffect(() => {
    if (value) updateSelected({ items: value, replace: true });
  }, [value]);

  useEffect(() => {
    const isInputValid = validateInput ? validateInput(searchInput) : true;
    setInputErrorState(isInputValid ? null : t('incorrectFormat'));
  }, [searchInput, t, validateInput]);

  return (
    <div
      className={classnames(styles.container, { [styles.disabled]: disabled }, className)}
      ref={containerRef}
      data-testid={testId}>
      <button name={name} className={styles.box} onClick={toggleIsOpen} disabled={disabled}>
        <span className={styles.label} data-testid={labelTestId}>
          {formatLabel(label, shouldLabelShowSelectedItems, selected)}
        </span>
      </button>
      <div className={classnames(styles.dropdown, { [styles.isOpen]: isOpen })}>
        {showFreeTextInput && (
          <Input
            placeholder={t('search')}
            onChange={(e) => setSearchInput(e.target.value)}
            errorMessage={inputErrorState ?? undefined}
            errorMessagePosition="bottom"
            containerClassName={styles.dropdownInputContainer}
          />
        )}
        <div className={styles.inner}>
          <ol className={styles.list}>{renderItems()}</ol>
          <Button
            text="Apply"
            small
            className={styles.applyButton}
            onClick={handleApply}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

export { CheckboxDropdown };
